// components/transcript/StrongAsTag.tsx
import React from 'react'
import type { Components } from 'react-markdown'
import ReactMarkdown from 'react-markdown'
import { tagClass } from '@/lib/transcriptFormat'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import guideContent, { ldElementsIndex } from '@/components/debateTools/guidebook/content'

const StrongAsTag: Components['strong'] = ({ children }) => {
  const raw = React.Children.toArray(children)
    .map((c) => (typeof c === 'string' ? c : ''))
    .join('')
  const m = raw.match(/^<([^>]+)>$/)
  if (!m) return <strong>{children}</strong>

  const tag = m[1]
  const base = tag.split('.')[0].toUpperCase()

  // Map transcript tag → dictionary key
  const mapTagToDictKey = (b: string): string | null => {
    if (b === 'ROADMAP') return 'roadmap'
    if (b === 'HOOK') return 'hook'
    if (b === 'FRAMEWORK') return 'framework'
    if (b === 'VALUE') return 'value'
    if (b === 'CRITERION') return 'criterion'
    if (b === 'PHILOSOPHICALJUSTIFICATION') return 'philosophy'
    if (b === 'DEFINITIONSANDOBSERVATIONS' || b === 'WORDPHRASEDEFINITIONS') return 'definitions'
    if (b === 'OBSERVATIONS' || b === 'SCOPESTOCKISSUES') return 'observations'
    if (b === 'CONTENTIONS') return 'contentions'
    if (/^CLAIM\d*$/i.test(b)) return 'claim'
    if (/^WARRANT\d*$/i.test(b)) return 'warrant'
    if (/^IMPACT\d*$/i.test(b)) return 'impact'
    if (/^SUBPOINTS?\d*$/i.test(b)) return 'subpoint'
    if (b === 'HIGHLIGHTS') return 'highlights'
    if (b === 'CRYSTALLIZINGCLOSINGVOTERSPREVIEW') return 'closing'
    return null
  }

  const dictKey = mapTagToDictKey(base)
  const dictItem = dictKey ? ldElementsIndex[dictKey] : undefined
  const chipLabel = dictItem?.label || base

  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          // NOTE:
          // - `!text-black dark:!text-black` ensures the tag text stays black even if
          //   a parent sets `dark:[&_*]:text-white` or similar.
          // - `tagClass(tag)` still controls the chip’s background/border colors.
          className={`inline-flex items-center rounded-md border px-1.5 py-0.5 font-mono text-[11px] font-semibold ${tagClass(
            tag,
          )} cursor-help !text-black dark:!text-black`}
          title={tag}
          aria-label={tag}
        >
          {chipLabel}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="max-w-[520px] whitespace-pre-wrap text-xs leading-5 [&_p]:my-0 [&_ul]:my-0 [&_li]:my-0"
      >
        <div className="font-semibold mb-1">{dictItem?.title || chipLabel}</div>
        {dictItem ? (
          <ReactMarkdown
            components={{
              p: ({ node, ...props }) => <p className="m-0" {...props} />,
              ul: ({ node, ...props }) => (
                <ul className="m-0 pl-4 list-disc" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="m-0 pl-4 list-decimal" {...props} />
              ),
              li: ({ node, ...props }) => <li className="m-0" {...props} />,
              strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
            }}
          >
            {(dictItem.content || '').replace(/\n\s*\n+/g, '\n').trim()}
          </ReactMarkdown>
        ) : (
          <div>{tag}</div>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

export default StrongAsTag
